import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'

interface NFSeDfeRow {
  chave:          string
  numero:         string
  cnpj_prestador: string
  nome_prestador: string
  cnpj_tomador:   string
  nome_tomador:   string
  papel_empresa:  string
  desc_servico:   string
  valor:          string
  dh_emissao:     string | null
  confirmada:     boolean
  rejeitada:      boolean
  cancelada:      boolean
  digitada:       boolean
  digitada_em:    string | null
  nDps:           string
  serieDps:       string
  competencia:    string
  finNfse:        string
  finNfseDes:     string
}

interface Filtros {
  dataInicio:   string
  dataFim:      string
  situacao:     string
  digitada:     string
  participante: string
  chave:        string
}

const inp: React.CSSProperties = {
  border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
  fontSize: '0.82rem', height: 30, background: '#fff',
}

const label: React.CSSProperties = {
  fontSize: '0.72rem', color: '#6b7280', fontWeight: 600,
}

function campo(rotulo: string, children: React.ReactNode) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <label style={label}>{rotulo}</label>
      {children}
    </div>
  )
}

// "Sim" ganha a cor do evento (verde confirmada, vermelho rejeitada, azul lançada); "Não" fica neutro.
const CORES_SELO = {
  verde: { bg: '#dcfce7', fg: '#15803d' },
  vermelho: { bg: '#fee2e2', fg: '#dc2626' },
  azul: { bg: '#dbeafe', fg: '#1d4ed8' },
}
function selo(ativo: boolean, cor: keyof typeof CORES_SELO = 'verde') {
  const c = CORES_SELO[cor]
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: 10,
      fontSize: '0.74rem', fontWeight: 600,
      background: ativo ? c.bg : '#f1f5f9',
      color: ativo ? c.fg : '#94a3b8',
    }}>
      {ativo ? 'Sim' : 'Não'}
    </span>
  )
}

// Faixa à esquerda da linha pela situação da nota.
function corSituacao(n: { confirmada: boolean; rejeitada: boolean; cancelada: boolean; digitada: boolean }) {
  if (n.cancelada) return '#991b1b'
  if (n.rejeitada) return '#dc2626'
  if (n.digitada) return '#1d4ed8'
  if (n.confirmada) return '#16a34a'
  return '#f59e0b'
}

const dividerStyle: React.CSSProperties = {
  width: 1, background: '#e2e8f0', alignSelf: 'stretch',
}

const thBase: React.CSSProperties = {
  padding: '8px 6px', whiteSpace: 'nowrap', color: '#fff', fontWeight: 700,
}

function stat(rotulo: string, valor: string | number, cor: string) {
  return (
    <div style={{ textAlign: 'center', minWidth: 90 }}>
      <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
        {rotulo}
      </div>
      <div style={{ fontSize: '1.05rem', fontWeight: 700, color: cor, fontVariantNumeric: 'tabular-nums' }}>
        {valor}
      </div>
    </div>
  )
}

function IconChave() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L21 7l-3-3m-3.5 3.5L17 4" />
    </svg>
  )
}

function fmtMoeda(v: string | number) {
  const n = typeof v === 'string' ? parseFloat(v) : v
  return (n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtData(iso: string | null) {
  if (!iso) return '—'
  return iso.slice(0, 10).split('-').reverse().join('/')
}

function fmtCompetencia(v: string) {
  if (!v || v.length < 7) return '—'
  const [ano, mes] = v.split('-')
  return `${mes}/${ano}`
}

function fmtDataHora(iso: string | null) {
  if (!iso) return ''
  const d = new Date(iso)
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
}

function fmtCnpjCpf(doc: string) {
  const n = (doc || '').replace(/\D/g, '')
  if (n.length === 14) return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  if (n.length === 11) return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  return doc
}

function ToolbarBtn({ title, bg, fg, border, children, disabled, destaque, onClick }: {
  title: string; bg: string; fg: string; border?: string; children: React.ReactNode; disabled?: boolean
  destaque?: boolean; onClick?: () => void
}) {
  return (
    <button
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 44, height: 44, borderRadius: '50%',
        border: `1px solid ${disabled ? '#e5e7eb' : destaque ? '#1a6ea8' : border ?? '#e2e8f0'}`,
        background: disabled ? '#f1f5f9' : destaque ? '#1a6ea8' : bg,
        color: disabled ? '#cbd5e1' : destaque ? '#fff' : fg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        boxShadow: destaque ? '0 0 0 4px rgba(26,110,168,0.25), 0 2px 6px rgba(0,0,0,0.15)' : '0 1px 2px rgba(0,0,0,0.06)',
        transform: destaque ? 'scale(1.08)' : undefined,
        transition: 'transform 0.15s, box-shadow 0.15s, background 0.15s',
      }}
    >
      {children}
    </button>
  )
}

const iconProps = {
  width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
  strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
}

function IconAtualizar() {
  return (
    <svg {...iconProps}>
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  )
}

function IconConfirmar() {
  return (
    <svg {...iconProps}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  )
}

function IconRejeitar() {
  return (
    <svg {...iconProps}>
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  )
}

function IconDownload() {
  return (
    <svg {...iconProps}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  )
}

function IconPdf() {
  return (
    <svg {...iconProps}>
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  )
}

function IconImportar() {
  return (
    <svg {...iconProps}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 8 12 3 17 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  )
}

const MOTIVOS_REJEICAO = [
  { cod: '1', label: '1 - NFS-e em duplicidade' },
  { cod: '2', label: '2 - NFS-e já emitida pelo tomador' },
  { cod: '3', label: '3 - Não ocorrência do fato gerador' },
  { cod: '4', label: '4 - Erro quanto à responsabilidade tributária' },
  { cod: '5', label: '5 - Erro quanto ao valor do serviço, deduções ou data do fato gerador' },
  { cod: '9', label: '9 - Outros' },
]

function filtroPadrao(): Filtros {
  const hoje = new Date()
  const inicio = new Date(hoje)
  inicio.setDate(inicio.getDate() - 7)
  return {
    dataInicio: inicio.toISOString().slice(0, 10),
    dataFim: hoje.toISOString().slice(0, 10),
    situacao: '', digitada: '', participante: '', chave: '',
  }
}

// Vindo do painel de Compras: ?de=&ate=&digitada=nao
function filtroInicial(): Filtros {
  const q = new URLSearchParams(window.location.search), p = filtroPadrao()
  return { ...p, dataInicio: q.get('de') || p.dataInicio, dataFim: q.get('ate') || p.dataFim, digitada: q.get('digitada') ?? p.digitada }
}

export default function ManifestadorNFSeNacional() {
  const { empresaCod, empresaAtual } = useEmpresa()
  const navigate = useNavigate()
  const [filtros, setFiltros] = useState<Filtros>(filtroInicial)
  const [pesquisaAtiva, setPesquisaAtiva] = useState<Filtros>(filtroInicial)
  const [lista, setLista] = useState<NFSeDfeRow[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set())
  const [chaveCaixa, setChaveCaixa] = useState('')
  const [atualizando, setAtualizando] = useState(false)
  const [msgAtualizar, setMsgAtualizar] = useState<{ texto: string; ok: boolean } | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [rejeitando, setRejeitando] = useState(false)
  const [cMotivo, setCMotivo] = useState('9')
  const [xMotivo, setXMotivo] = useState('')
  const [modalEventos, setModalEventos] = useState<{
    chave: string; carregando: boolean; erro: string | null
    eventos: { tipo: string; dhRegEvento: string; nPedRegEvento: string; cMotivo: string; xMotivo: string; xml?: string }[]
  } | null>(null)
  const [xmlEventoAberto, setXmlEventoAberto] = useState<number | null>(null)

  async function abrirEventos(chave: string) {
    setXmlEventoAberto(null)
    setModalEventos({ chave, carregando: true, erro: null, eventos: [] })
    try {
      const r = await api.get(`/manifestador-nfse-nacional/${chave}/eventos`)
      setModalEventos({ chave, carregando: false, erro: null, eventos: r.data.eventos })
    } catch (e: any) {
      setModalEventos({ chave, carregando: false, erro: e?.response?.data?.error || 'Erro ao consultar eventos', eventos: [] })
    }
  }

  const carregar = useCallback(async () => {
    if (!empresaCod) return
    setCarregando(true)
    setErro(null)
    try {
      const params: Record<string, string> = { empCod: String(empresaCod) }
      if (pesquisaAtiva.dataInicio)   params.dataInicio   = pesquisaAtiva.dataInicio
      if (pesquisaAtiva.dataFim)      params.dataFim      = pesquisaAtiva.dataFim
      if (pesquisaAtiva.situacao)     params.situacao     = pesquisaAtiva.situacao
      if (pesquisaAtiva.digitada)     params.digitada     = pesquisaAtiva.digitada
      if (pesquisaAtiva.participante) params.participante = pesquisaAtiva.participante
      if (pesquisaAtiva.chave)        params.chave        = pesquisaAtiva.chave
      const r = await api.get('/manifestador-nfse-nacional', { params })
      // Pendentes de lançamento (vindo do painel): cancelada não é lançada
      const semCanceladas = pesquisaAtiva.digitada === 'nao' && new URLSearchParams(window.location.search).get('digitada') === 'nao'
      setLista(semCanceladas ? r.data.filter((n: NFSeDfeRow) => !n.cancelada) : r.data)
      setSelecionados(new Set())
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao carregar lista')
    } finally {
      setCarregando(false)
    }
  }, [empresaCod, pesquisaAtiva])

  useEffect(() => { carregar() }, [carregar])

  function pesquisar() { setPesquisaAtiva({ ...filtros }) }
  function limparFiltros() { const p = filtroPadrao(); setFiltros(p); setPesquisaAtiva(p) }

  async function atualizarLista() {
    if (!empresaCod || atualizando) return
    setAtualizando(true)
    setMsgAtualizar(null)
    try {
      const r = await api.post('/manifestador-nfse-nacional/atualizar', { empCod: empresaCod })
      const res = r.data
      if (res.erro) {
        setMsgAtualizar({ texto: res.erro, ok: false })
      } else if (res.status === 'DOCUMENTOS_LOCALIZADOS') {
        setMsgAtualizar({ texto: `${res.novas} NFS-e(s) nova(s) encontrada(s).`, ok: true })
      } else if (res.status === 'NENHUM_DOCUMENTO_LOCALIZADO') {
        setMsgAtualizar({ texto: 'Nenhum documento novo encontrado.', ok: true })
      } else {
        setMsgAtualizar({ texto: 'Não foi possível atualizar a lista agora.', ok: false })
      }
      await carregar()
    } catch (e: any) {
      setMsgAtualizar({ texto: e?.response?.data?.error || 'Erro ao atualizar lista', ok: false })
    } finally {
      setAtualizando(false)
    }
  }

  function toggleSel(chave: string) {
    setSelecionados(s => {
      const novo = new Set(s)
      if (novo.has(chave)) novo.delete(chave); else { novo.clear(); novo.add(chave) }
      return novo
    })
  }

  // Marcou uma nota: já joga a chave no campo de baixo, pra poder copiar ou usar
  // como alvo dos botões de ação — igual o Manifestador do Destinatário (NF-e).
  useEffect(() => {
    setRejeitando(false)
    setChaveCaixa(selecionados.size === 1 ? [...selecionados][0] : '')
  }, [selecionados])

  const chaveAlvo = chaveCaixa.replace(/\D/g, '')
  const notaAlvo = lista.find(n => n.chave === chaveAlvo)
  const podeManifestar = chaveAlvo.length === 50 && !notaAlvo?.confirmada && !notaAlvo?.rejeitada && !notaAlvo?.cancelada
  const podeBaixarXml  = chaveAlvo.length === 50
  // Digitada não trava o download/manifestação — só o "Importar" mesmo (não
  // faz sentido lançar a mesma nota duas vezes no Lançamento de Notas).
  const podeImportar   = chaveAlvo.length === 50 && !notaAlvo?.digitada && !notaAlvo?.cancelada

  function baixarXml() {
    if (!podeBaixarXml) return
    window.open(`/api/manifestador-nfse-nacional/${chaveAlvo}/xml`, '_blank')
  }

  function baixarDanfse() {
    if (!podeBaixarXml) return
    window.open(`/api/manifestador-nfse-nacional/${chaveAlvo}/danfse`, '_blank')
  }

  function importarSelecionada() {
    if (!podeImportar) return
    navigate(`/compras/recebimentos/lancamento-nfse99?importarChave=${chaveAlvo}`)
  }

  async function confirmarSelecionada() {
    if (!podeManifestar || enviando) return
    setEnviando(true)
    setMsgAtualizar(null)
    try {
      const r = await api.post(`/manifestador-nfse-nacional/${chaveAlvo}/confirmar`, { empcod: empresaCod })
      if (r.data.ok) {
        setMsgAtualizar({ texto: 'Confirmação registrada com sucesso.', ok: true })
        setLista(l => l.map(row => row.chave === chaveAlvo ? { ...row, confirmada: true } : row))
      } else {
        setMsgAtualizar({ texto: r.data.erro || 'Não foi possível registrar a confirmação.', ok: false })
      }
    } catch (e: any) {
      setMsgAtualizar({ texto: e?.response?.data?.error || 'Erro ao registrar a confirmação.', ok: false })
    } finally {
      setEnviando(false)
    }
  }

  async function rejeitarSelecionada() {
    if (!podeManifestar || enviando) return
    if (xMotivo.trim().length < 15) {
      setMsgAtualizar({ texto: 'A justificativa precisa ter pelo menos 15 caracteres.', ok: false })
      return
    }
    setEnviando(true)
    setMsgAtualizar(null)
    try {
      const r = await api.post(`/manifestador-nfse-nacional/${chaveAlvo}/rejeitar`, {
        empcod: empresaCod, cMotivo, xMotivo: xMotivo.trim(),
      })
      if (r.data.ok) {
        setMsgAtualizar({ texto: 'Rejeição registrada com sucesso.', ok: true })
        setLista(l => l.map(row => row.chave === chaveAlvo ? { ...row, rejeitada: true } : row))
        setRejeitando(false)
        setXMotivo('')
      } else {
        setMsgAtualizar({ texto: r.data.erro || 'Não foi possível registrar a rejeição.', ok: false })
      }
    } catch (e: any) {
      setMsgAtualizar({ texto: e?.response?.data?.error || 'Erro ao registrar a rejeição.', ok: false })
    } finally {
      setEnviando(false)
    }
  }

  const confirmadas = lista.filter(n => n.confirmada)
  const pendentes   = lista.filter(n => !n.confirmada && !n.rejeitada && !n.cancelada)
  const totalConfirmadas = confirmadas.reduce((s, n) => s + parseFloat(n.valor || '0'), 0)
  const totalPendentes   = pendentes.reduce((s, n) => s + parseFloat(n.valor || '0'), 0)

  return (
    <div>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '0 0 0.75rem' }}>
        Manifestador de NFS-e Nacional
      </h2>

      {/* Filtros */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'flex-end',
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
        padding: '0.75rem 0.9rem', marginBottom: '1rem',
      }}>
        {campo('EMPRESA', (
          <div style={{ ...inp, width: 220, display: 'flex', alignItems: 'center', background: '#f3f4f6', color: '#6b7280' }}>
            {empresaAtual ? `${empresaAtual.empcod} - ${empresaAtual.empfan || empresaAtual.empnom}` : '—'}
          </div>
        ))}
        {campo('EMISSÃO DE', <input style={{ ...inp, width: 150 }} type="date" value={filtros.dataInicio}
          onChange={e => setFiltros(f => ({ ...f, dataInicio: e.target.value }))} />)}
        {campo('ATÉ', <input style={{ ...inp, width: 150 }} type="date" value={filtros.dataFim}
          onChange={e => setFiltros(f => ({ ...f, dataFim: e.target.value }))} />)}
        {campo('SITUAÇÃO', (
          <select style={{ ...inp, width: 130 }} value={filtros.situacao}
            onChange={e => setFiltros(f => ({ ...f, situacao: e.target.value }))}>
            <option value="">Todas</option>
            <option value="confirmada">Confirmada</option>
            <option value="rejeitada">Rejeitada</option>
            <option value="pendente">Pendente</option>
          </select>
        ))}
        {campo('DIGITADA', (
          <select style={{ ...inp, width: 130 }} value={filtros.digitada}
            onChange={e => setFiltros(f => ({ ...f, digitada: e.target.value }))}>
            <option value="">Todas</option>
            <option value="sim">Digitadas</option>
            <option value="nao">Não digitadas</option>
          </select>
        ))}
        {campo('PARTICIPANTE (CNPJ)', (
          <input style={{ ...inp, width: 160 }} value={filtros.participante}
            placeholder="CNPJ do prestador"
            onChange={e => setFiltros(f => ({ ...f, participante: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && pesquisar()} />
        ))}
        {campo('CHAVE OU Nº DA NFS-e', (
          <input style={{ ...inp, width: 340 }} value={filtros.chave}
            placeholder="Chave de 50 dígitos ou só o número"
            onChange={e => setFiltros(f => ({ ...f, chave: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && pesquisar()} />
        ))}
        <button className="btn btn-primary btn-sm" onClick={pesquisar} disabled={carregando}
          style={{ height: 30, alignSelf: 'flex-end' }}>
          {carregando ? 'Buscando...' : 'Pesquisar'}
        </button>
        <button className="btn btn-sm" onClick={limparFiltros} style={{ height: 30, alignSelf: 'flex-end', color: '#6b7280' }}>
          Limpar
        </button>
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}
      {msgAtualizar && (
        <div className={`alert ${msgAtualizar.ok ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: '0.75rem' }}>
          {msgAtualizar.texto}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'flex-start' }}>
        <div style={{ flex: '0 1 auto', minWidth: 0 }}>
          {/* Grid */}
          <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <table className="table" style={{ fontSize: '0.78rem', minWidth: 1000, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#1a6ea8' }}>
                  <th style={{ ...thBase, width: 30 }}></th>
                  <th style={{ ...thBase, width: 80 }}>Número</th>
                  <th style={{ ...thBase, width: 130 }}>Prestador</th>
                  <th style={{ ...thBase }}>Nome Prestador</th>
                  <th style={{ ...thBase, width: 70 }}>DPS</th>
                  <th style={{ ...thBase, width: 70 }}>Série DPS</th>
                  <th style={{ ...thBase, width: 80 }}>Competência</th>
                  <th style={{ ...thBase, width: 140 }}>Finalidade</th>
                  <th style={{ ...thBase }}>Descrição</th>
                  <th style={{ ...thBase, width: 80 }}>Emissão</th>
                  <th style={{ ...thBase, width: 100, textAlign: 'right' }}>Valor R$</th>
                  <th style={{ ...thBase, width: 90, textAlign: 'center' }}>Confirmada</th>
                  <th style={{ ...thBase, width: 90, textAlign: 'center' }}>Rejeitada</th>
                  <th style={{ ...thBase, width: 82, textAlign: 'center' }}>Lançada</th>
                  <th style={{ ...thBase, width: 130 }}>Lançada em</th>
                </tr>
              </thead>
              <tbody>
                {carregando && (
                  <tr><td colSpan={15} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Carregando...</td></tr>
                )}
                {!carregando && lista.length === 0 && (
                  <tr><td colSpan={15} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Nenhuma NFS-e encontrada</td></tr>
                )}
                {lista.map((n, idx) => (
                  <tr key={n.chave} onDoubleClick={() => abrirEventos(n.chave)} title="Duplo clique para ver os eventos enviados"
                    style={{ borderBottom: '1px solid #d5dfeb', background: selecionados.has(n.chave) ? '#cfe6fb' : idx % 2 === 1 ? '#edf3fa' : '#fff', cursor: 'pointer' }}>
                    <td style={{ textAlign: 'center', padding: '6px', borderLeft: `4px solid ${corSituacao(n)}` }}>
                      <input type="checkbox" checked={selecionados.has(n.chave)} onChange={() => toggleSel(n.chave)} />
                    </td>
                    <td style={{ fontWeight: 700, padding: '6px', color: '#1a6ea8' }}>
                      {n.digitada && n.numero ? (
                        <span onClick={() => navigate(`/compras/consultas/notas?numero=${Number(n.numero) || n.numero}`)}
                          style={{ cursor: 'pointer', color: '#1d4ed8', textDecoration: 'underline' }}
                          title="Já digitada — clique para consultar a nota">
                          {n.numero}
                        </span>
                      ) : (n.numero || '—')}
                      {n.cancelada && (
                        <span title="Cancelada pelo prestador — não pode ser lançada" style={{
                          marginLeft: 6, padding: '1px 6px', borderRadius: 10, fontSize: '0.7rem',
                          fontWeight: 700, background: '#fee2e2', color: '#991b1b',
                        }}>Cancelada</span>
                      )}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.74rem', whiteSpace: 'nowrap', padding: '6px' }}>{fmtCnpjCpf(n.cnpj_prestador)}</td>
                    <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '6px' }} title={n.nome_prestador}>
                      {n.nome_prestador}
                    </td>
                    <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>{n.nDps || '—'}</td>
                    <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>{n.serieDps || '—'}</td>
                    <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>{fmtCompetencia(n.competencia)}</td>
                    <td style={{ padding: '6px', whiteSpace: 'nowrap' }} title={n.finNfseDes}>
                      {n.finNfseDes || (n.finNfse ? `Código ${n.finNfse}` : '—')}
                    </td>
                    <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '6px' }} title={n.desc_servico}>
                      {n.desc_servico}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', padding: '6px' }}>{fmtData(n.dh_emissao)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, padding: '6px' }}>{fmtMoeda(n.valor)}</td>
                    <td style={{ textAlign: 'center', padding: '6px' }}>{selo(n.confirmada, 'verde')}</td>
                    <td style={{ textAlign: 'center', padding: '6px' }}>{selo(n.rejeitada, 'vermelho')}</td>
                    <td style={{ textAlign: 'center', padding: '6px' }}>{selo(n.digitada, 'azul')}</td>
                    <td style={{ whiteSpace: 'nowrap', color: '#6b7280', padding: '6px' }}>{n.digitada && n.digitada_em ? fmtDataHora(n.digitada_em) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Resumo */}
          <div style={{
            display: 'flex', justifyContent: 'center', alignItems: 'stretch', flexWrap: 'wrap', gap: '1.75rem',
            border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 8px 8px',
            background: '#f8fafc', padding: '0.85rem 1.25rem',
          }}>
            {stat('Confirmadas', confirmadas.length, '#2563eb')}
            <div style={dividerStyle} />
            {stat('Total Confirmado R$', fmtMoeda(totalConfirmadas), '#2563eb')}
            <div style={dividerStyle} />
            {stat('Pendentes', pendentes.length, pendentes.length > 0 ? '#b45309' : '#0f172a')}
            <div style={dividerStyle} />
            {stat('Total Pendente R$', fmtMoeda(totalPendentes), pendentes.length > 0 ? '#b45309' : '#0f172a')}
          </div>

          {/* Chave da NFS-e */}
          <div style={{
            marginTop: '0.75rem', border: '1px solid #e2e8f0', borderRadius: 8,
            background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
            padding: '0.9rem 1rem', textAlign: 'center',
          }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
              <IconChave />
              <input
                style={{ ...inp, width: 420, fontFamily: 'monospace' }}
                value={chaveCaixa}
                onChange={e => { setSelecionados(new Set()); setChaveCaixa(e.target.value.replace(/\D/g, '')) }}
                placeholder="Chave de acesso da NFS-e Nacional (50 dígitos)"
                maxLength={50}
              />
            </div>
            <span style={{ fontSize: '0.76rem', color: '#94a3b8' }}>
              Marque uma nota na lista ou cole a chave aqui, depois use os botões ao lado
            </span>

            {rejeitando && (
              <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '0.75rem', paddingTop: '0.75rem', textAlign: 'left' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 480, margin: '0 auto' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <label style={label}>MOTIVO DA REJEIÇÃO</label>
                    <select style={inp} value={cMotivo} onChange={e => setCMotivo(e.target.value)}>
                      {MOTIVOS_REJEICAO.map(m => <option key={m.cod} value={m.cod}>{m.label}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <label style={label}>JUSTIFICATIVA (15 a 255 caracteres)</label>
                    <textarea
                      style={{ ...inp, height: 60, resize: 'vertical' as const, paddingTop: 6 }}
                      value={xMotivo}
                      maxLength={255}
                      onChange={e => setXMotivo(e.target.value)}
                    />
                    <span style={{ fontSize: '0.7rem', color: xMotivo.trim().length < 15 ? '#dc2626' : '#9ca3af' }}>
                      {xMotivo.trim().length}/255 {xMotivo.trim().length < 15 ? '(mínimo 15)' : ''}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-danger-solid btn-sm" onClick={rejeitarSelecionada} disabled={enviando}>
                      {enviando ? 'Enviando...' : 'Confirmar Rejeição'}
                    </button>
                    <button className="btn btn-sm" onClick={() => setRejeitando(false)} disabled={enviando} style={{ color: '#6b7280' }}>
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Barra de ações */}
        <div style={{
          display: 'flex', flexDirection: 'column', gap: '0.6rem',
          padding: '0.5rem', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff',
        }}>
          <ToolbarBtn
            title={atualizando ? 'Consultando ADN...' : 'Atualizar Lista'}
            bg="#334155" fg="#fff" border="#334155"
            disabled={atualizando}
            onClick={atualizarLista}
          >
            <IconAtualizar />
          </ToolbarBtn>
          <ToolbarBtn
            title={podeManifestar ? 'Confirmar Operação' : 'Marque uma nota pendente pra confirmar'}
            bg="#15803d" fg="#fff" border="#15803d" destaque={podeManifestar}
            disabled={!podeManifestar || enviando}
            onClick={confirmarSelecionada}
          ><IconConfirmar /></ToolbarBtn>
          <ToolbarBtn
            title={podeManifestar ? 'Rejeitar' : 'Marque uma nota pendente pra rejeitar'}
            bg="#b91c1c" fg="#fff" border="#b91c1c"
            disabled={!podeManifestar || enviando}
            onClick={() => setRejeitando(true)}
          ><IconRejeitar /></ToolbarBtn>
          <ToolbarBtn
            title={podeBaixarXml ? 'Baixar DANFSe (PDF)' : 'Marque uma nota pra baixar o DANFSe'}
            bg="#334155" fg="#fff" border="#334155"
            disabled={!podeBaixarXml}
            onClick={baixarDanfse}
          ><IconPdf /></ToolbarBtn>
          <ToolbarBtn
            title={podeBaixarXml ? 'Baixar XML' : 'Marque uma nota pra baixar o XML'}
            bg="#334155" fg="#fff" border="#334155"
            disabled={!podeBaixarXml}
            onClick={baixarXml}
          ><IconDownload /></ToolbarBtn>
          <ToolbarBtn
            title={podeImportar ? 'Importar XML pro Lançamento de Notas' : notaAlvo?.cancelada ? 'NFS-e cancelada pelo prestador' : notaAlvo?.digitada ? 'Essa nota já foi lançada' : 'Marque uma nota pra importar'}
            bg="#334155" fg="#fff" border="#334155" destaque={podeImportar}
            disabled={!podeImportar}
            onClick={importarSelecionada}
          ><IconImportar /></ToolbarBtn>
        </div>
      </div>

      {modalEventos && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          zIndex: 1000, padding: '16px 10px',
        }} onClick={() => setModalEventos(null)}>
          <div style={{
            background: '#fff', borderRadius: 8,
            width: 560, maxWidth: '97vw', maxHeight: 'calc(100vh - 32px)', overflow: 'auto',
            boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              background: '#1a6ea8', color: '#fff',
              padding: '0.85rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>Eventos enviados</div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', opacity: 0.85 }}>{modalEventos.chave}</div>
              </div>
              <button onClick={() => setModalEventos(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1.2rem', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: '1rem 1.25rem' }}>
              {modalEventos.carregando && (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Consultando eventos no ADN...</div>
              )}
              {!modalEventos.carregando && modalEventos.erro && (
                <div style={{ textAlign: 'center', padding: '1rem', color: '#b91c1c' }}>{modalEventos.erro}</div>
              )}
              {!modalEventos.carregando && !modalEventos.erro && modalEventos.eventos.length === 0 && (
                <div style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>Nenhum evento de manifestação enviado pra essa nota ainda.</div>
              )}
              {!modalEventos.carregando && modalEventos.eventos.map((ev, i) => (
                <div key={i} style={{
                  border: '1px solid #e2e8f0', borderRadius: 6, padding: '0.75rem 1rem',
                  marginBottom: i < modalEventos.eventos.length - 1 ? '0.6rem' : 0,
                }}>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a6ea8', marginBottom: 4 }}>{ev.tipo}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem 1rem', fontSize: '0.78rem' }}>
                    <div><span style={{ color: '#6b7280' }}>Data/Hora: </span>{ev.dhRegEvento ? fmtDataHora(ev.dhRegEvento) : '—'}</div>
                    <div><span style={{ color: '#6b7280' }}>Protocolo: </span>{ev.nPedRegEvento || '—'}</div>
                    {ev.cMotivo && <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#6b7280' }}>Motivo: </span>{ev.cMotivo}</div>}
                    {ev.xMotivo && <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#6b7280' }}>Justificativa: </span>{ev.xMotivo}</div>}
                  </div>
                  {ev.xml && (
                    <div style={{ marginTop: 6 }}>
                      <button
                        onClick={() => setXmlEventoAberto(a => a === i ? null : i)}
                        style={{ background: 'none', border: 'none', color: '#1a6ea8', fontSize: '0.74rem', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                      >
                        {xmlEventoAberto === i ? 'Ocultar XML do evento' : 'Ver XML do evento'}
                      </button>
                      {xmlEventoAberto === i && (
                        <pre style={{
                          marginTop: 6, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4,
                          padding: '0.5rem', fontSize: '0.68rem', maxHeight: 220, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                        }}>{ev.xml}</pre>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
